import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Play, ExternalLink, Loader2, Sparkles, Music2, Video, Camera, Tv, Filter, X } from 'lucide-react';

/* ====================================================================
 *  REAL TALK
 *  Mickey's TikTok + Facebook video stream, curated for the website.
 * ==================================================================== */

const PLATFORM_META = {
  tiktok:    { label: 'TikTok',    color: '#000000', Icon: Music2 },
  facebook:  { label: 'Facebook',  color: '#1877F2', Icon: Video },
  instagram: { label: 'Instagram', color: '#E1306C', Icon: Camera },
  youtube:   { label: 'YouTube',   color: '#FF0000', Icon: Tv },
  other:     { label: 'Video',     color: '#666',    Icon: Play },
};

const isVertical = (platform) => platform === 'tiktok' || platform === 'instagram';

export default function RealTalk() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get('/videos').then((r) => r.data?.items || []),
      api.get('/videos/categories').then((r) => r.data?.items || []),
    ])
      .then(([v, c]) => { if (mounted) { setVideos(v); setCategories(c); } })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    document.title = 'Real Talk · Kickass Course';
    return () => { document.title = 'Kickass Course'; };
  }, []);

  const platformsPresent = useMemo(() => {
    const s = new Set(videos.map((v) => v.platform));
    return [...s].filter(Boolean);
  }, [videos]);

  const featured = useMemo(() => videos.find((v) => v.featured && v.published), [videos]);

  const filtered = useMemo(() => videos.filter((v) => {
    if (filterCategory !== 'all' && (v.category || '') !== filterCategory) return false;
    if (filterPlatform !== 'all' && v.platform !== filterPlatform) return false;
    if (featured && v.id === featured.id) return false; // featured is shown separately
    return true;
  }), [videos, filterCategory, filterPlatform, featured]);

  const closeModal = useCallback(() => setOpenVideo(null), []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    if (openVideo) {
      window.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [openVideo, closeModal]);

  // Smart card-click: link-out mode opens URL directly (no modal); embed mode opens modal.
  const handleCardOpen = useCallback((v) => {
    const mode = v.embed_mode || (v.platform === 'tiktok' || v.platform === 'youtube' ? 'embed' : 'link_out');
    if (mode === 'link_out') {
      const win = window.open(v.url, '_blank', 'noopener,noreferrer');
      if (win) win.opener = null;
    } else {
      setOpenVideo(v);
    }
  }, []);

  return (
    <div className="bg-white" data-testid="real-talk-page">
      {/* === HERO === */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
          backgroundImage: 'linear-gradient(rgba(212,164,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,164,55,0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}/>
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(212,164,55,0.18), transparent 70%)',
        }}/>
        <div className="relative kc-container py-16 md:py-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold mb-5">
            <Sparkles size={12}/> No filter. No fluff.
          </div>
          <h1 className="font-display uppercase leading-[0.88]" style={{ fontSize: 'clamp(3rem, 9vw, 7rem)' }} data-testid="real-talk-title">
            REAL <span className="text-[color:var(--kc-gold)]">TALK</span>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
            The raw clips, the daily drops, the unscripted takes. Straight from my TikTok and Facebook — pulled together here so you can binge the mindset without the algorithm getting in the way.
          </p>
        </div>
      </section>

      {/* === FEATURED === */}
      {featured && (
        <section className="kc-container -mt-10 md:-mt-12 relative z-10" data-testid="featured-video">
          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)] overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="relative bg-ink min-h-[320px] md:min-h-[480px] flex items-center justify-center group cursor-pointer" onClick={() => handleCardOpen(featured)}>
                {featured.thumbnail ? (
                  <img src={featured.thumbnail} alt={featured.title || 'featured'} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity"/>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--kc-gold)]/40 via-ink to-ink"/>
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[color:var(--kc-gold)] text-black inline-flex items-center justify-center shadow-[0_20px_50px_-10px_rgba(212,164,55,0.7)] group-hover:scale-110 transition-transform">
                    <Play size={36} fill="currentColor" className="ml-1"/>
                  </div>
                </div>
                <div className="absolute top-3 left-3">
                  <PlatformPill platform={featured.platform}/>
                </div>
                <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] font-bold bg-[color:var(--kc-gold)] text-black px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                  <Sparkles size={10}/> Featured
                </div>
              </div>
              <div className="p-6 md:p-10 flex flex-col justify-center">
                {featured.category && categories.find((c) => c.slug === featured.category) && (
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--kc-gold-2)] font-semibold mb-2">
                    {categories.find((c) => c.slug === featured.category)?.name}
                  </div>
                )}
                <h2 className="font-display uppercase text-3xl md:text-4xl leading-tight" data-testid="featured-title">
                  {featured.title || 'Watch this one.'}
                </h2>
                {featured.description && (
                  <p className="mt-4 text-base md:text-lg text-muted-kc leading-relaxed">{featured.description}</p>
                )}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button onClick={() => handleCardOpen(featured)} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] hover:gap-3 transition-all shadow-[0_15px_40px_-10px_rgba(212,164,55,0.4)]" data-testid="featured-play">
                    <Play size={18} fill="currentColor"/> Watch now
                  </button>
                  <a href={featured.url} target="_blank" rel="noreferrer" className="h-12 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border-2 border-kc-strong text-ink font-semibold hover:bg-surface-2 transition-colors">
                    Watch on {PLATFORM_META[featured.platform]?.label || 'platform'} <ExternalLink size={14}/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* === FILTERS + GRID === */}
      <section className="kc-container py-16">
        {/* Filters */}
        <div className="flex flex-col gap-4 mb-8" data-testid="filters">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-kc font-semibold">
            <Filter size={11}/> Filter
          </div>
          <div className="flex flex-wrap gap-2" data-testid="filter-category">
            <FilterChip active={filterCategory === 'all'} onClick={() => setFilterCategory('all')}>All</FilterChip>
            {categories.map((c) => (
              <FilterChip key={c.id} active={filterCategory === c.slug} onClick={() => setFilterCategory(c.slug)}>{c.name}</FilterChip>
            ))}
          </div>
          {platformsPresent.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="filter-platform">
              <FilterChip active={filterPlatform === 'all'} onClick={() => setFilterPlatform('all')} variant="ghost">All platforms</FilterChip>
              {platformsPresent.map((p) => {
                const Ic = PLATFORM_META[p]?.Icon || Play;
                return (
                  <FilterChip key={p} active={filterPlatform === p} onClick={() => setFilterPlatform(p)} variant="ghost">
                    <Ic size={11}/> {PLATFORM_META[p]?.label || p}
                  </FilterChip>
                );
              })}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-muted-kc" size={28}/></div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] bg-surface-2">
            <Play size={36} className="mx-auto text-muted-kc mb-3"/>
            <div className="font-display uppercase text-xl mb-1">Coming soon</div>
            <div className="text-sm text-muted-kc">Mickey’s real talk drops are being curated — check back shortly.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-sm text-muted-kc">No videos match this filter.</div>
            <button onClick={() => { setFilterCategory('all'); setFilterPlatform('all'); }} className="mt-3 text-sm font-semibold underline hover:text-[color:var(--kc-gold-2)]">Clear filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="videos-grid">
            {filtered.map((v) => (
              <VideoCard key={v.id} video={v} categories={categories} onOpen={() => handleCardOpen(v)}/>
            ))}
          </div>
        )}
      </section>

      {/* === MODAL === */}
      {openVideo && <VideoModal video={openVideo} onClose={closeModal}/>}
    </div>
  );
}

/* ============================================================ */
const FilterChip = ({ active, onClick, children, variant = 'solid' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold border transition-colors ${
      active
        ? 'bg-ink text-white border-ink'
        : variant === 'ghost'
          ? 'bg-white text-muted-kc border-kc hover:border-kc-strong hover:text-ink'
          : 'bg-surface-2 text-muted-kc border-transparent hover:bg-white hover:border-kc-strong hover:text-ink'
    }`}
    data-testid={`chip-${(typeof children === 'string' ? children : 'p').toString().toLowerCase().replace(/\s+/g,'-')}`}
  >
    {children}
  </button>
);

const PlatformPill = ({ platform }) => {
  const meta = PLATFORM_META[platform] || PLATFORM_META.other;
  const Ic = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.18em] font-bold">
      <Ic size={10}/> {meta.label}
    </span>
  );
};

function VideoCard({ video, categories, onOpen }) {
  const vertical = isVertical(video.platform);
  const aspect = vertical ? 'aspect-[9/16]' : 'aspect-video';
  const cat = video.category && categories.find((c) => c.slug === video.category);
  const linkOut = (video.embed_mode || (isVertical(video.platform) || video.platform === 'youtube' ? 'embed' : 'link_out')) === 'link_out';
  return (
    <div className="group" data-testid={`video-card-${video.id}`}>
      <button
        type="button"
        onClick={onOpen}
        className={`block w-full ${aspect} relative overflow-hidden rounded-[var(--kc-radius-md)] bg-ink border border-kc hover:border-[color:var(--kc-gold)] transition-colors`}
        aria-label={`${linkOut ? 'Open' : 'Play'} ${video.title || 'video'}`}
      >
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title || ''} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--kc-gold)]/30 via-ink to-ink"/>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[color:var(--kc-gold)]/95 text-black inline-flex items-center justify-center shadow-[0_15px_30px_-8px_rgba(212,164,55,0.55)] group-hover:scale-110 transition-transform">
            {linkOut ? <ExternalLink size={20}/> : <Play size={22} fill="currentColor" className="ml-1"/>}
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <PlatformPill platform={video.platform}/>
        </div>
        {video.featured && (
          <div className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.18em] font-bold bg-[color:var(--kc-gold)] text-black px-2 py-0.5 rounded-full">★</div>
        )}
        {(video.title || cat) && (
          <div className="absolute inset-x-0 bottom-0 p-3 text-white text-left">
            {cat && <div className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--kc-gold)] font-bold mb-0.5">{cat.name}</div>}
            <div className="text-sm font-semibold line-clamp-2 leading-snug">{video.title || ''}</div>
          </div>
        )}
      </button>
    </div>
  );
}

function VideoModal({ video, onClose }) {
  const vertical = isVertical(video.platform);
  // Prefer the direct iframe URL (most reliable, no scripts needed).
  // Fallback to oEmbed HTML for legacy records.
  const iframeSrc = video.iframe_src || '';
  const platformName = PLATFORM_META[video.platform]?.label || 'platform';
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-5xl bg-ink rounded-[var(--kc-radius-md)] shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black text-white inline-flex items-center justify-center transition-colors"
          data-testid="video-modal-close"
        >
          <X size={18}/>
        </button>
        <div className={`${vertical ? 'md:w-[420px] aspect-[9/16]' : 'flex-1 aspect-video'} bg-black w-full flex-shrink-0`}>
          {iframeSrc ? (
            <iframe
              src={iframeSrc}
              title={video.title || 'video'}
              className="w-full h-full border-0"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid="video-modal-iframe"
            />
          ) : video.embed_html ? (
            <div className="w-full h-full kc-embed" dangerouslySetInnerHTML={{ __html: video.embed_html }}/>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-white/70 text-center px-6">
              <Play size={48} className="mb-3 opacity-70"/>
              <div className="text-sm">Playback isn’t available inline.</div>
              <a href={video.url} target="_blank" rel="noreferrer" className="mt-4 h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-sm">
                Open on {platformName} <ExternalLink size={14}/>
              </a>
            </div>
          )}
        </div>
        <div className="p-5 md:p-7 flex-1 text-white overflow-y-auto">
          <PlatformPill platform={video.platform}/>
          {video.title && <h3 className="mt-3 font-display uppercase text-2xl md:text-3xl leading-tight">{video.title}</h3>}
          {video.description && <p className="mt-3 text-sm md:text-base text-white/75 leading-relaxed">{video.description}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a href={video.url} target="_blank" rel="noreferrer" className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors text-sm" data-testid="video-modal-open-on-platform">
              Open on {platformName} <ExternalLink size={14}/>
            </a>
            {video.platform === 'facebook' && (
              <span className="text-[11px] text-white/60 italic">Tip: if the player above shows “Video Unavailable”, click Open on Facebook — many FB videos require their native player.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
